import React, { useState, useCallback, ReactNode, useEffect } from 'react';
import useToggle from 'react-use-v13/lib/useToggle';
import _ from 'lodash';
import Bluebird from 'bluebird';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// @ts-ignore
import ms from 'ms';

import Emoji from 'design-system/Emoji';
// TODO: Use new button
// eslint-disable-next-line import-path/forbidden
import Button from 'design-system/ButtonLegacy';
import { InputCheckbox, Label } from 'design-system/Form';
import { Modal, ModalHeader } from 'design-system/Modal';
import { getLidarNickname } from '@scale/shared/customers';
import { captureClientSideError } from '@scale/frontend-lib/errors';
import { ReviewLevel, TaskReviewOutcome, twoDTypes } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import {
  TaskerModalHeader,
  TaskerModalHeaderIcon,
  TaskerModalBody,
  TaskerModalFooter,
} from 'react-scale/components/scalers/tasks/common/TaskerModal';
import { CorruptionSummary } from 'react-scale/components/scalers/tasks/CorruptionWarningModal';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { useSendModalShortcut } from 'react-scale/hooks/useSubmitButtonShortcuts';
import logger from '@scale/frontend-lib/logging';
import { FetchSmeStatus } from '../common/UnleashUtils';

export enum SendTypes {
  Submit,
  Approve,
  Reject,
  ApproveWithChanges,
  SendBackToQueue,
  SendBackToQueueWithChanges,
  Continue,
}

export type SendHandler = (
  sendTypes?: SendTypes,
  isReportedForSensitiveContent?: boolean,
  reason?: string,
  explanation?: string,
) => Promise<void>;

export function sendTypeToReviewOutcome(sendType: SendTypes) {
  switch (sendType) {
    case SendTypes.Approve:
      return TaskReviewOutcome.Accepted;
    case SendTypes.ApproveWithChanges:
      return TaskReviewOutcome.Fixed;
    case SendTypes.Reject:
    case SendTypes.SendBackToQueue:
    case SendTypes.SendBackToQueueWithChanges:
      return TaskReviewOutcome.Rejected;
    default:
      return undefined;
  }
}

export enum SendStatus {
  Prompt,
  PromptApproved,
  Send,
  Sending,
  Sent,
  Failed,
}

export const TEXTS: Record<SendTypes, Partial<Record<SendStatus, string>>> = {
  [SendTypes.SendBackToQueueWithChanges]: {
    [SendStatus.Send]: 'Send Back To Queue With Changes',
    [SendStatus.Sending]: 'Send Back To Queue With Changes',
    [SendStatus.Sent]: 'Sent Back To Queue With Changes',
    [SendStatus.Failed]: 'Send Back To Queue With Changes Failed',
  },
  [SendTypes.Submit]: {
    [SendStatus.Prompt]: 'Submit Task',
    [SendStatus.PromptApproved]: 'Submit Task',
    [SendStatus.Send]: 'Submit Task',
    [SendStatus.Sending]: 'Submitting',
    [SendStatus.Sent]: 'Submitted',
    [SendStatus.Failed]: 'Submit Failed',
  },
  [SendTypes.Approve]: {
    [SendStatus.Send]: 'Approve Task',
    [SendStatus.Sending]: 'Approving',
    [SendStatus.Sent]: 'Approved',
    [SendStatus.Failed]: 'Approve Failed',
  },
  [SendTypes.Reject]: {
    [SendStatus.Send]: 'Reject Task',
    [SendStatus.Sending]: 'Rejecting',
    [SendStatus.Sent]: 'Rejected',
    [SendStatus.Failed]: 'Reject Failed',
  },
  [SendTypes.ApproveWithChanges]: {
    [SendStatus.Send]: 'Approve With Changes',
    [SendStatus.Sending]: 'Approving With Changes',
    [SendStatus.Sent]: 'Approved With Changes',
    [SendStatus.Failed]: 'Approve With Changes Failed',
  },
  [SendTypes.SendBackToQueue]: {
    [SendStatus.Send]: 'Send Back To Queue',
    [SendStatus.Sending]: 'Sending Back To Queue',
    [SendStatus.Sent]: 'Sent Back To Queue',
    [SendStatus.Failed]: 'Send Back To Queue Failed',
  },
  [SendTypes.Continue]: {
    [SendStatus.Send]: 'Continue',
    [SendStatus.Sending]: 'Continue',
    [SendStatus.Sent]: 'Continue',
    [SendStatus.Failed]: 'Could Not Continue',
  },
};

const SHOW_CONFIRMATION_TIME_THRESHOLD = 6000;

interface ISubmitConfirmTaskPromptModal {
  onCancel: () => void;
  setSendStatus: (value: SendStatus) => void;
  sendStatus: SendStatus;
}

function SubmitConfirmTaskPromptModal({
  onCancel,
  setSendStatus,
  sendStatus,
}: ISubmitConfirmTaskPromptModal) {
  const [checked, toggleChecked] = useToggle(false);
  const storageKey = 'local-storage-never-ask-submit-confirmation';
  const handleConfirmation = useCallback(() => {
    if (checked) {
      localStorage.setItem(storageKey, 'true');
    }
    setSendStatus(SendStatus.PromptApproved);
  }, [setSendStatus, checked]);

  useEffect(() => {
    if (localStorage.getItem(storageKey) === 'true') {
      handleConfirmation();
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleConfirmation();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sendStatus, handleConfirmation]);

  return (
    <Modal
      contentLabel="ConfirmationPrompt"
      maxWidth="500px"
      isOpen={true}
      onRequestClose={onCancel}
    >
      <ModalHeader onRequestClose={onCancel}>Are you sure you want to submit?</ModalHeader>
      <div
        style={{
          paddingTop: 16,
          paddingLeft: 42,
          paddingRight: 42,
          paddingBottom: 32,
          fontSize: 16,
          lineHeight: 2,
        }}
      >
        <div className="submission-confirmation__checkbox">
          <Label style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <InputCheckbox checked={checked} onChange={toggleChecked} />
            <span className="submission-confirmation__guidelines">Never ask me again</span>
          </Label>
        </div>
        <div className="submission-confirmation__row">
          <Button type="primary" onClick={() => handleConfirmation()}>
            Yes
          </Button>
          <Button type="primary" onClick={() => onCancel?.()}>
            No
          </Button>
        </div>
      </div>
      <style jsx>{`
        .submission-confirmation__checkbox {
          flex: 1 0 68%;
          margin-right: 2%;
        }
        .submission-confirmation__guidelines {
          margin-left: 5px;
          display: inline-block;
        }
        .submission-confirmation__row {
          margin-top: 16px;
          display: flex;
          justify-content: center;
          gap: 20px;
        }
      `}</style>
    </Modal>
  );
}

interface ISendTaskModalProps {
  annotationCount?: number;
  children: (props: { disabled: boolean; onClick: () => void; text: string }) => JSX.Element;
  disableModal?: boolean;
  estimate?: number;
  nextTaskHandler?: () => void;
  preSendChecks?: () => Promise<boolean> | boolean;
  sendHandler: SendHandler;
  taskProjectId?: string;
  getTimeSpent?: () => { taskTimeSpent: number };
  setTimeSpent?: () => void;
  taskType: TaskType;
  taskUserId: string;
  type: SendTypes;
  lowAccuracyCorruptionTypes?: string[];
  improvedAccuracyCorruptionTypes?: string[];
  corruptionAccuracy?: number;
  autoloadNextAssignment?: boolean;
  requireConfirm?: boolean;
  isTestQuestion?: boolean;
  hideOnFail?: boolean;
  postCompletionFeedback?: string;
  shortcutEnabled?: boolean;
  setPostSubmissionGraph?: (value: boolean) => void;
  // for lidar annotation async submission only: flag to support async submissions - this is only set to true when the response and grade are populated
  attemptSubmittedAndGraded?: boolean;
  taskId?: string;
  assignmentId?: string;
  reviewLevel?: ReviewLevel;
  shouldPreventSend?: boolean;
  userId?: string;
}

// eslint-disable-next-line complexity
export function SendTaskModal({
  annotationCount = 0,
  children,
  disableModal = false,
  estimate = 8 * 1000, // 8s
  nextTaskHandler,
  preSendChecks,
  sendHandler,
  taskProjectId,
  getTimeSpent,
  setTimeSpent,
  taskType,
  taskUserId,
  type,
  lowAccuracyCorruptionTypes,
  improvedAccuracyCorruptionTypes,
  corruptionAccuracy,
  autoloadNextAssignment = false,
  isTestQuestion,
  hideOnFail,
  postCompletionFeedback,
  shortcutEnabled,
  attemptSubmittedAndGraded,
  setPostSubmissionGraph,
  taskId,
  assignmentId,
  reviewLevel,
  shouldPreventSend = false,
  userId,
}: ISendTaskModalProps) {
  const [processingModalAction, setProcessingModalAction] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sendStatus, setSendStatus] = useState(SendStatus.Send);
  const isSme: boolean = userId ? FetchSmeStatus(userId) : false;

  const shouldShowSubmitConfirmation = useUnleashFeatureFlag(
    'show-submit-confirmation',
    { projectId: taskProjectId },
    false,
  );
  const shouldShowPostSubmissionGraph = useUnleashFeatureFlag(
    'show-post-submission-graph',
    { projectId: taskProjectId },
    false,
  );

  const onCancel = () => {
    setSendStatus(SendStatus.Send);
  };

  // eslint-disable-next-line complexity
  const onClick = useCallback(async () => {
    if (type === SendTypes.Submit && sendStatus !== SendStatus.PromptApproved && !isSme) {
      setSendStatus(SendStatus.Prompt);
      return;
    }

    if (
      twoDTypes.includes(taskType) &&
      shouldShowSubmitConfirmation &&
      // Check if time spent on task exceeds 1 minutes
      (setTimeSpent?.() ??
        (getTimeSpent?.()?.taskTimeSpent ?? 0) > SHOW_CONFIRMATION_TIME_THRESHOLD)
    )
      return;

    if (preSendChecks && !(await preSendChecks())) return;

    const startTime = new Date().getTime();
    setSendStatus(SendStatus.Sending);

    const interval = setInterval(() => {
      const nowTime = new Date().getTime();
      const distanceMs = Math.floor(nowTime - startTime);
      const newProgress = Math.floor((distanceMs * 100) / estimate);

      if (newProgress <= 99) {
        setProgress(newProgress);
      }
    }, 50);

    try {
      await sendHandler(type);
      clearInterval(interval);
      // This block is for tutorial practice attempts - we do not submit fully, but rather grade and block on frontend for feedback
      if (shouldPreventSend === true) {
        setSendStatus(SendStatus.Send);
      }
      // block for lidar annotation tasks that are async (the useeffect above will catch this after async finishes)
      else if (taskType !== TaskType.LidarAnnotation) {
        setProgress(100);
        await Bluebird.delay(200); // NOTE: this delay is only for aesthetic purposes.
        if (disableModal && autoloadNextAssignment) {
          // For short tasks auto load next assignment.
          setSendStatus(SendStatus.Sent);
          if (shouldShowPostSubmissionGraph) {
            setPostSubmissionGraph?.(true);
          } else {
            nextTaskHandler?.();
          }
        } else if (disableModal) {
          // For /training and /tests.
          setSendStatus(SendStatus.Send);
        } else {
          setSendStatus(SendStatus.Sent);
        }
      }
    } catch (err) {
      clearInterval(interval);

      // TODO: err is being logged by `sendHandler`. Handle it here.
      setSendStatus(SendStatus.Failed);

      // NOTE: these delays are only for aesthetic purposes.
      if (hideOnFail && !isTestQuestion) {
        await Bluebird.delay(3000);
        closeModal();
      }
      if (disableModal) {
        await Bluebird.delay(3000);
        setSendStatus(SendStatus.Send);
      }

      captureClientSideError(err, 'approve-with-changes-failed', {
        extras: {
          taskUserId,
          taskProjectId,
        },
      });

      logger.error('Approve attempt failure.', {
        errorType: 'approve-attempt-failed',
        error: err,
        errorMessage: err.message,
        errorStack: err.stack,
        taskUserId,
        taskProjectId,
        taskId,
        assignmentId,
        reviewLevel,
      });

      throw err;
    }
  }, [
    preSendChecks,
    estimate,
    sendHandler,
    disableModal,
    autoloadNextAssignment,
    nextTaskHandler,
    taskType,
    shouldShowPostSubmissionGraph,
    taskId,
    assignmentId,
    reviewLevel,
    sendStatus,
    type,
  ]);

  useEffect(() => {
    // for lidar annotation async submission only: update send status once async submission finishes and the response has been populated
    if (
      taskType === TaskType.LidarAnnotation &&
      attemptSubmittedAndGraded &&
      sendStatus === SendStatus.Sending
    ) {
      setProgress(100);
      setSendStatus(SendStatus.Sent);
    }
    // for confirmation submission prompt approved status: trigger the onClick handler
    if (type === SendTypes.Submit && sendStatus === SendStatus.PromptApproved) {
      onClick();
    }
  }, [sendStatus, attemptSubmittedAndGraded, taskType, type, onClick]);

  useSendModalShortcut(onClick, type, !shortcutEnabled || sendStatus !== SendStatus.Send); // sendStatus !== SendStatus.Send is disabled in children buttons

  const disableModalActions = useCallback(() => {
    setProcessingModalAction(true);
  }, []);

  const onConfirmClick = useCallback(() => {
    if (isTestQuestion) return closeModal();
    disableModalActions();
    nextTaskHandler?.();
  }, [nextTaskHandler, disableModalActions]);

  const closeModal = useCallback(() => {
    setProcessingModalAction(false);
    setSendStatus(SendStatus.Send);
  }, []);

  const { name, emoji } = getLidarNickname(taskUserId, taskProjectId, taskType);
  const showEmoji = taskUserId || taskProjectId || taskType;
  const annotationName = getAnnotationName(taskType, annotationCount);
  const showCorruptionSummary =
    !_.isUndefined(lowAccuracyCorruptionTypes) ||
    !_.isUndefined(improvedAccuracyCorruptionTypes) ||
    !_.isUndefined(corruptionAccuracy);
  const taskTimeSpentHuman = ms(getTimeSpent ? getTimeSpent().taskTimeSpent : 0);
  return (
    <React.Fragment>
      {type === SendTypes.Submit && sendStatus === SendStatus.Prompt && (
        <SubmitConfirmTaskPromptModal
          onCancel={onCancel}
          setSendStatus={setSendStatus}
          sendStatus={sendStatus}
        />
      )}
      <Modal
        isOpen={!disableModal && sendStatus !== SendStatus.Send}
        maxWidth={480}
        top="50%"
        transform="translateY(-50%)"
        margin="auto"
      >
        <TaskerModalHeader>
          {sendStatus === SendStatus.Sending && (
            <TaskerModalHeaderIcon icon="sync-alt" color="#008EFF" />
          )}
          {sendStatus === SendStatus.Sent && (
            <TaskerModalHeaderIcon icon="check-circle" color="#008EFF" />
          )}
          {sendStatus === SendStatus.Failed && (
            <TaskerModalHeaderIcon icon="exclamation-triangle" color="#008EFF" />
          )}
          {sendStatus === SendStatus.Sending &&
            `Your task is ${
              TEXTS?.[type]?.[SendStatus.Sending] ?? ''
            }.toLowerCase()... please do not refresh or close this window`}
          {sendStatus === SendStatus.Sent &&
            `Your task was successfully ${TEXTS?.[type]?.[SendStatus.Sent] ?? ''}.toLowerCase()}!`}
          {sendStatus === SendStatus.Failed && TEXTS?.[type]?.[SendStatus.Failed]}
        </TaskerModalHeader>

        <TaskerModalBody>
          {!!showEmoji && (
            <div className="body__item">
              <Emoji emoji={emoji} /> {name}
            </div>
          )}
          {!!annotationCount && !!annotationName && (
            <div className="body__item">
              <FontAwesomeIcon icon="user-tag" /> {annotationCount} {annotationName}
            </div>
          )}
          <div className="body__item">
            <FontAwesomeIcon icon="clock" /> {taskTimeSpentHuman} taken
          </div>
        </TaskerModalBody>

        {showCorruptionSummary && (
          <TaskerModalBody>
            <CorruptionSummary
              lowAccuracyCorruptionTypes={lowAccuracyCorruptionTypes}
              improvedAccuracyCorruptionTypes={improvedAccuracyCorruptionTypes}
              corruptionAccuracy={corruptionAccuracy}
              taskType={taskType}
            />
          </TaskerModalBody>
        )}

        {sendStatus === SendStatus.Sent && postCompletionFeedback && (
          <TaskerModalBody>
            <div style={{ fontWeight: 'normal', color: 'black' }}>
              <SanitizedMarkdown text={postCompletionFeedback} />
            </div>
          </TaskerModalBody>
        )}

        <TaskerModalFooter>
          {sendStatus === SendStatus.Sending && (
            <React.Fragment>
              <div className="progress">
                <div className="progress__bar" style={{ width: `${progress}%` }} />
                <div className="progress__text">{progress}%</div>
              </div>
            </React.Fragment>
          )}
          {sendStatus === SendStatus.Sent && (
            <div className="footer__item">
              <Button
                type="button"
                onClick={onConfirmClick}
                primary
                style={{ width: '100%' }}
                disabled={processingModalAction}
              >
                {isTestQuestion ? 'Review My Results' : 'Next Task'}
              </Button>
            </div>
          )}
          {sendStatus === SendStatus.Failed && (
            <div className="footer__item">
              <Button
                type="button"
                onClick={closeModal}
                primary
                style={{ width: '100%' }}
                disabled={processingModalAction}
              >
                Close
              </Button>
            </div>
          )}
        </TaskerModalFooter>
      </Modal>

      {children({
        disabled: sendStatus !== SendStatus.Send,
        onClick,
        text: TEXTS?.[type]?.[sendStatus] ?? 'Next (error)',
      })}

      <style jsx>{`
        .body__item:not(:last-child) {
          margin-right: 25px;
        }
        .footer__item {
          flex: 1 0 49%;
        }
        .footer__item:not(:last-child) {
          margin-right: 2%;
        }
        .progress {
          background: rgba(0, 142, 255, 0.5);
          border-radius: 8px;
          height: 34px;
          overflow: hidden;
          position: relative;
          width: 100%;
        }
        .progress__bar {
          background: #008eff;
          border-radius: 8px;
          height: 100%;
          left: 0;
          position: absolute;
          top: 0;
        }
        .progress__text {
          color: #fff;
          font-size: 11px;
          font-weight: 500;
          height: 100%;
          line-height: 34px;
          position: relative;
          text-align: center;
          width: 100%;
          z-index: 1;
        }
      `}</style>
    </React.Fragment>
  );
}

// eslint-disable-next-line complexity
function getAnnotationName(taskType: TaskType, count: number): string {
  switch (taskType) {
    case TaskType.Annotation:
    case TaskType.VideoBoxAnnotation:
      if (count === 1) {
        return 'box';
      }
      return 'boxes';
    case TaskType.AudioTranscription:
      if (count === 1) {
        return 'word';
      }
      return 'words';
    case TaskType.Categorization:
      if (count === 1) {
        return 'taxonomy';
      }
      return 'taxonomies';
    case TaskType.Comparison:
    case TaskType.DataCollection:
      if (count === 1) {
        return 'field';
      }
      return 'fields';
    case TaskType.LaneAnnotation:
      if (count === 1) {
        return 'lane';
      }
      return 'lanes';
    case TaskType.CuboidAnnotation:
    case TaskType.LidarAnnotation:
    case TaskType.LidarLinking:
    case TaskType.LidarStitching:
    case TaskType.VideoCuboidAnnotation:
      if (count === 1) {
        return 'cuboid';
      }
      return 'cuboids';
    case TaskType.LineAnnotation:
      if (count === 1) {
        return 'line';
      }
      return 'lines';
    case TaskType.PointAnnotation:
      if (count === 1) {
        return 'point';
      }
      return 'points';
    case TaskType.PolygonAnnotation:
      if (count === 1) {
        return 'polygon';
      }
      return 'polygons';
    case TaskType.Transcription:
      if (count === 1) {
        return 'transcription';
      }
      return 'transcriptions';
    case TaskType.LidarSegmentation:
    case TaskType.SegmentAnnotation:
      if (count === 1) {
        return 'point';
      }
      return 'points';
    case TaskType.FixAnnotation:
    case TaskType.NamedEntityRecognition:
    case TaskType.PhoneCall:
    default:
      return 'annotations';
  }
}

interface ISendTaskButton {
  appearance?: 'default' | 'lidar';
  arrows?: boolean;
  disabled: boolean;
  hasMargin?: boolean;
  onClick: () => void;
  children: ReactNode;
  tooltip: string | null;
  type: SendTypes;
}

export function SendTaskButton({
  appearance = 'default',
  arrows = false,
  disabled,
  hasMargin = true,
  onClick,
  children,
  tooltip,
  type,
}: ISendTaskButton) {
  let buttonProps: Record<string, any> = {};
  if (appearance === 'default' && (type === SendTypes.Submit || type === SendTypes.Continue)) {
    buttonProps = {
      style: { marginLeft: hasMargin ? '15px' : '0' },
      primary: true,
      disabled,
      onClick,
      tooltip,
      type: 'button',
    };
  }
  if (appearance === 'lidar' && type === SendTypes.Submit) {
    buttonProps = {
      primary: true,
      disabled,
      onClick,
      tooltip,
      type: 'button',
      arrows,
      size: 'lg',

      className: 'full',
    };
  }
  return (
    <>
      <div data-tip={tooltip || 'Submit task when complete'} data-for="submit-tooltip">
        <Button {...buttonProps}>{children}</Button>
      </div>
      <ReactTooltip id="submit-tooltip" delayShow={250} />
    </>
  );
}
